


































































import { Vue, Component, Prop } from 'vue-property-decorator';
import BasicSelect from '@/components/common/BasicSelect.vue';
import QuestionListItemGroup from '@/views/question/components/QuestionListItem/QuestionListItemGroup.vue';
import SubQuestionSelectModal from '@/views/question/components/SubQuestionSelectModal.vue';
import SubQuestionConditionModal from '@/views/question/components/SubQuestionConditionModal.vue';
import {
  ISubQuestionConfig,
  ISubQuestionCondition,
  createDefaultSubQuestionConfig,
  shouldShowConditionModal,
  shouldShowQuestionLevel,
  LEVEL_OPTIONS,
  stripMappingDuplicateConditions,
  resolveInitialSelectedIdsForAnswerTrigger,
  getExpectedSubQuestionDepth,
  resolveSelectableSubQuestionDepth,
} from '@/views/question/constants/sub-question';
import { QUESTION_TYPES } from '@/views/question/constants';
import { cloneDeep } from 'lodash';

@Component({
  components: {
    BasicSelect,
    QuestionListItemGroup,
    SubQuestionSelectModal,
    SubQuestionConditionModal,
  },
})
export default class SubQuestionSection extends Vue {
  @Prop({ required: true }) question!: any;
  @Prop({ default: () => [] }) allQuestions!: any[];
  @Prop({ default: false }) readOnly!: boolean;

  get selectModalName(): string {
    return `sub-question-select-${this.question.id}`;
  }

  get conditionModalName(): string {
    return `sub-question-condition-${this.question.id}`;
  }

  get subQuestionConfig(): ISubQuestionConfig {
    const config = this.question.sub_question_config || createDefaultSubQuestionConfig();
    return config;
  }

  get selectedSubQuestions(): any[] {
    const ids = this.subQuestionConfig.sub_question_ids || [];
    return this.allQuestions.filter((q) => ids.includes(q.id));
  }

  get showConditionModalButton(): boolean {
    return shouldShowConditionModal(this.question.type);
  }

  get showQuestionLevel(): boolean {
    return shouldShowQuestionLevel(this.question.type);
  }

  get levelOptions() {
    return LEVEL_OPTIONS;
  }

  get currentInitialSelectedIds(): Array<number | string> {
    if (this.answerTrigger !== null && this.answerTrigger !== undefined) {
      return resolveInitialSelectedIdsForAnswerTrigger(
        String(this.answerTrigger),
        this.subQuestionConfig,
      );
    }
    return this.subQuestionConfig.sub_question_ids || [];
  }

  get selectableQuestions(): any[] {
    const config = this.subQuestionConfig;
    const currentSubIds = config.sub_question_ids || [];

    // Find all IDs that are already assigned as sub-questions to OTHER questions
    const assignedToOthers = new Set<string | number>();
    this.allQuestions.forEach((q) => {
      if (String(q.id) === String(this.question.id)) {
        return;
      }
      const subIds = q.sub_question_config?.sub_question_ids || [];
      subIds.forEach((id) => assignedToOthers.add(String(id)));
    });

    return this.allQuestions.filter((q) => {
      // 0. Always include currently selected sub-questions for THIS parent
      // to ensure labels are displayed correctly in the select box
      if (currentSubIds.some((sid) => String(sid) === String(q.id))) {
        return true;
      }

      // 1. Exclude the current question
      if (String(q.id) === String(this.question.id)) {
        return false;
      }

      // 2. If it's already a sub-question of ANOTHER question, hide it
      if (assignedToOthers.has(String(q.id))) {
        return false;
      }

      // 3. Filter by depth: only show questions one level below this parent
      const expectedChildDepth = getExpectedSubQuestionDepth(this.question);
      const qDepth = resolveSelectableSubQuestionDepth(q.id, this.allQuestions);
      return qDepth === expectedChildDepth;
    });
  }

  get refSubQuestionSelectModal() {
    return this.$refs.subQuestionSelectModal as any;
  }

  answerTrigger: string | null = null;
  showNotice: boolean = false;

  get hasConditions(): boolean {
    return (
      this.subQuestionConfig.conditions
      && this.subQuestionConfig.conditions.length > 0
    );
  }

  get conditionLabel(): string {
    if (this.hasConditions) {
      return `${this.subQuestionConfig.conditions.length} 件の条件設定済み`;
    }
    return '条件を設定';
  }

  get hasSubQuestionsSelected(): boolean {
    return (this.subQuestionConfig.sub_question_ids || []).length > 0;
  }

  get questionOptions(): any[] {
    const { CHECKBOX, RADIO } = QUESTION_TYPES;
    if ([CHECKBOX, RADIO].includes(this.question.type)) {
      return this.question.options || [];
    }
    return [];
  }

  openSelectModal(answerTrigger: string | null = null) {
    this.answerTrigger = answerTrigger;
    const initialIds =
      answerTrigger !== null && answerTrigger !== undefined
        ? resolveInitialSelectedIdsForAnswerTrigger(
          String(answerTrigger),
          this.subQuestionConfig,
        )
        : [...(this.subQuestionConfig.sub_question_ids || [])];

    // [SCOPE] Bug #9: truyền initialIds qua modal params — tránh race khi $modal.show
    // chạy trước khi Vue re-render prop initialSelectedIds (answerTrigger cũ = null
    // → fallback toàn bộ sub_question_ids).
    this.$nextTick(() => {
      this.$modal.show(this.selectModalName, { initialIds });
    });
  }

  openConditionModal() {
    if (!this.hasSubQuestionsSelected) {
      this.showNotice = true;
      return;
    }
    this.showNotice = false;
    this.$modal.show(this.conditionModalName);
  }

  goToTutorial() {
    this.$router.push({ name: 'question.tutorial' });
  }

  handleSelectModalCancel() {
    this.answerTrigger = null;
  }

  handleConditionModalCancel() {
    // Modal restores its own draft; parent state is unchanged.
  }

  handleSubQuestionsSelected(ids: Array<string | number>) {
    const config = cloneDeep(this.subQuestionConfig);
    const value = this.answerTrigger;

    if (value !== null && value !== undefined) {
      if (!config.answer_mapping) {
        config.answer_mapping = {};
      }

      // Track which sub-questions were previously assigned to this answer,
      // so we can clean up stale conditions when they are replaced (not just cleared).
      const previousIds: Array<string | number> = config.answer_mapping[value] || [];
      const removedIds = previousIds.filter(
        (oldId) => !ids.some((newId) => String(newId) === String(oldId)),
      );

      config.answer_mapping[value] = [...ids];

      // Keep per-answer mapping exclusive: subs assigned here are removed from other answers
      Object.keys(config.answer_mapping).forEach((key) => {
        if (String(key) === String(value) || !config.answer_mapping) {
          return;
        }
        config.answer_mapping[key] = config.answer_mapping[key].filter(
          (sid) => !ids.some((id) => String(id) === String(sid)),
        );
        if (config.answer_mapping[key].length === 0) {
          delete config.answer_mapping[key];
        }
      });

      if (ids.length === 0) {
        // Clear: remove per-answer mapping only.
        // Do NOT auto-delete config.conditions here — conditions are managed
        // independently by the user via the condition modal (handleConditionsSaved).
        // Removing conditions here would silently destroy user-set global conditions
        // that happen to reference the same answer value (Bug #2 fix).
        delete config.answer_mapping[value];
      } else if (removedIds.length > 0) {
        // Replace: Only delete conditions if NO sub-questions remain for the answer.
        // Don't delete conditions just because one sub-question was removed — check if
        // other sub-questions are still assigned to this answer value.
        const remainingMappedIds = config.answer_mapping?.[value] || [];
        if (remainingMappedIds.length === 0) {
          config.conditions = (config.conditions || []).filter(
            (c) =>
              !(
                String(c.answer_value) === String(value) &&
                ['is', 'in', 'not_is', 'not_in'].includes(c.comparison)
              ),
          );
        }
      }

      this.answerTrigger = null;
    } else {
      config.sub_question_ids = ids;
      // Clean up answer_mapping for IDs that were removed from the global list
      if (config.answer_mapping) {
        Object.keys(config.answer_mapping).forEach((key) => {
          const mapping = config.answer_mapping?.[key];
          if (mapping && config.answer_mapping) {
            config.answer_mapping[key] = mapping.filter((id) =>
              ids.some((sid) => String(sid) === String(id)),
            );
          }
        });

        // Remove empty mappings and their related conditions
        Object.keys(config.answer_mapping).forEach((key) => {
          if (config.answer_mapping && config.answer_mapping[key].length === 0) {
            delete config.answer_mapping[key];
            config.conditions = (config.conditions || []).filter(
              (c) => !(String(c.answer_value) === String(key) && ['is', 'in', 'not_is', 'not_in'].includes(c.comparison)),
            );
          }
        });
      }
    }

    const allMappedIds = Object.values(config.answer_mapping || {}).flat();

    if (value !== null && value !== undefined) {
      // [SCOPE] Per-answer (+) modal: chỉ lấy IDs từ answer_mapping, không union
      // sub_question_ids cũ — tránh giữ child đã gỡ (Bug #2/#5 depth graph drift).
      config.sub_question_ids = Array.from(new Set(allMappedIds));
    } else {
      const globalIds = Array.isArray(config.sub_question_ids) ? config.sub_question_ids : [];
      config.sub_question_ids = Array.from(new Set([...allMappedIds, ...globalIds]));
    }

    // If no sub-questions remain, clear all conditions and mappings
    if (config.sub_question_ids.length === 0) {
      config.conditions = [];
      config.answer_mapping = {};
    }

    this.emitUpdate(config);
  }

  handleConditionsSaved(conditions: ISubQuestionCondition[]) {
    const config = cloneDeep(this.subQuestionConfig);
    config.conditions = conditions;
    this.emitUpdate(config);
  }

  handleLevelChange(level: number) {
    const config = cloneDeep(this.subQuestionConfig);
    config.level = level;
    this.emitUpdate(config);
  }

  removeSubQuestion(id: number | string) {
    const config = cloneDeep(this.subQuestionConfig);
    config.sub_question_ids = config.sub_question_ids.filter(
      (subId) => String(subId) !== String(id),
    );

    // Clean up answer_mapping entries that reference the removed sub-question
    if (config.answer_mapping) {
      Object.keys(config.answer_mapping).forEach((key) => {
        if (config.answer_mapping) {
          config.answer_mapping[key] = config.answer_mapping[key].filter(
            (sid) => String(sid) !== String(id),
          );
          // If this answer no longer has any mapped sub-questions, remove the mapping and related conditions
          if (config.answer_mapping[key].length === 0) {
            delete config.answer_mapping[key];
            config.conditions = (config.conditions || []).filter(
              (c) => !(String(c.answer_value) === String(key) && ['is', 'in', 'not_is', 'not_in'].includes(c.comparison)),
            );
          }
        }
      });
    }

    // If no sub-questions remain, clear all conditions and mappings
    if (config.sub_question_ids.length === 0) {
      config.conditions = [];
      config.answer_mapping = {};
    }

    this.emitUpdate(config);
  }

  private emitUpdate(config: ISubQuestionConfig) {
    const normalized = stripMappingDuplicateConditions(this.question.type, config);
    this.$emit('onUpdateSubConfig', cloneDeep(normalized));
  }
}
